import { useCallback } from 'react';
import { Image3D } from '~/domain/patient/image3d';
import { FrameOfReferencePair, Patient } from '~/domain/patient/patient';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useActivateRegistration } from '~/hooks/registration/useRegistrationStore';
import { usePrimaryRowComparison } from '~/hooks/viewer/usePrimaryRowComparison';
import { useRegistrationDescribingTheScreen } from '~/hooks/viewer/useSelectedDeformationField';

export interface RegistrationComparison {
    readonly moving: Image3D;
    readonly relating?: RigidRegistration;
    readonly resampled?: Image3D;
}

export function comparisonSeriesFor(
    patient: ComparablePatient,
    registration: FrameOfReferencePair,
    image: Image3D,
    resampled: Image3D | undefined
): { moving: Image3D; relating?: RigidRegistration } | undefined {
    const acquired = patient.findCounterpartImage(registration, image);
    if (acquired) {
        const relating = patient
            .findRegistrationsBetween(image, acquired)
            .at(0);
        return relating ? { moving: acquired, relating } : undefined;
    }
    return resampled ? { moving: resampled } : undefined;
}

export type ComparablePatient = Pick<
    Patient,
    'findCounterpartImage' | 'findRegistrationsBetween'
>;

export function useRegistrationComparison():
    | RegistrationComparison
    | undefined {
    const patient = useSelectedPatient();
    const primaryImage = useSelectedPrimaryImage();
    const registration = useRegistrationDescribingTheScreen();

    if (!patient || !primaryImage || !registration) {
        return undefined;
    }
    const candidates = patient.images.filter(
        (image) =>
            image.isSpatiallyResampled &&
            image.frameOfReferenceUid === primaryImage.frameOfReferenceUid
    );
    // Recorded provenance first: geometry alone cannot tell two runs apart.
    const produced = candidates.filter((image) =>
        image.wasResampledBy(registration.sopInstanceUid)
    );
    const ranked = produced.length > 0 ? produced : candidates;
    const resampled =
        ranked.find((image) => image.resamplingOrigin === 'applied') ??
        ranked[0];

    const pair = comparisonSeriesFor(
        patient,
        registration,
        primaryImage,
        resampled
    );
    return pair ? { ...pair, resampled } : undefined;
}

export function useShowRegistrationComparison(): (() => void) | undefined {
    const comparison = useRegistrationComparison();
    const registration = useRegistrationDescribingTheScreen();
    const { show: showPair } = usePrimaryRowComparison();
    const activateRegistration = useActivateRegistration();

    const show = useCallback(() => {
        if (!comparison || !registration) {
            return;
        }
        if (!showPair(comparison.moving, comparison.relating, 'apart')) {
            return;
        }
        // The pair fuses through the relating matrix; the one under review stays selected.
        activateRegistration(
            registration.sopInstanceUid,
            comparison.moving.seriesUid
        );
    }, [comparison, registration, showPair, activateRegistration]);

    return comparison ? show : undefined;
}
